import { useState, type CSSProperties } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { BeamFrame } from "@pontimed/ui/puls";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, BellOff, CheckCircle2, Loader2, type LucideIcon } from "lucide-react";

import type { OptOutResponse } from "@/components/booking-request/types";
import { Button } from "@/components/ui/button";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { showToast } from "@/lib/ui/toast";

interface OptOutSearch {
  token?: string;
}

/**
 * Opt-out landing for the footer link of every external booking-request
 * message (decision D9). One confirm button → the provider stops receiving
 * requests and their public profile stops offering the request CTA.
 */
export const Route = createFileRoute("/requests/opt-out")({
  validateSearch: (search: Record<string, unknown>): OptOutSearch => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
  component: BookingRequestOptOutPage,
});

function BookingRequestOptOutPage() {
  const { token } = Route.useSearch();
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  // Who stopped receiving requests — the token decides (a doctor's own link
  // or a clinic's). Null until confirmed.
  const [doneScope, setDoneScope] = useState<OptOutResponse["scope"] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useNoIndexMeta();

  const submit = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    const toast = showToast(t`Saving your preference…`, "loading");
    try {
      const res = await apiFetch.post<OptOutResponse>(
        `/api/booking-requests/opt-out/${encodeURIComponent(token)}`,
        undefined,
        { skipAuthRefresh: true },
      );
      setDoneScope(res?.scope === "facility" ? "facility" : "provider");
      toast.update(t`You won't receive booking requests anymore`, "success");
    } catch (err) {
      const message =
        err instanceof ApiError && (err.status === 404 || err.status === 410)
          ? t`This link is no longer valid. Open the most recent message we sent you.`
          : t`Could not save your preference. Please try again.`;
      setError(message);
      toast.update(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={`${STATUS_CARD} text-center`} style={STATUS_CARD_STYLE}>
        {doneScope ? (
          <div className="flex flex-col items-center gap-4">
            <StatusTile icon={CheckCircle2} tone="success" />
            <h1 className={STATUS_TITLE} style={STAGGER_1}>
              <Trans>You're opted out</Trans>
            </h1>
            <p className={STATUS_BODY} style={STAGGER_2}>
              {doneScope === "facility" ? (
                <Trans>Your clinic won't receive booking requests from PontiMed anymore.</Trans>
              ) : (
                <Trans>You won't receive booking requests from PontiMed anymore.</Trans>
              )}
            </p>
          </div>
        ) : !token ? (
          <div className="flex flex-col items-center gap-4">
            <StatusTile icon={AlertCircle} tone="destructive" />
            <h1 className={STATUS_TITLE} style={STAGGER_1}>
              <Trans>Link not valid</Trans>
            </h1>
            <p className={STATUS_BODY} style={STAGGER_2}>
              <Trans>Open the opt-out link straight from the message we sent you.</Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <StatusTile icon={BellOff} tone="primary" />
            <h1 className={STATUS_TITLE} style={STAGGER_1}>
              <Trans>Stop receiving booking requests?</Trans>
            </h1>
            <p className={STATUS_BODY} style={STAGGER_2}>
              <Trans>
                Patients will no longer be able to send you appointment requests through PontiMed,
                and we'll stop contacting you about them.
              </Trans>
            </p>

            {error && (
              <p
                className="border-destructive-border bg-destructive-soft text-destructive-strong m-0 w-full rounded-lg border px-3 py-2.5 text-sm"
                role="alert"
              >
                {error}
              </p>
            )}

            <BeamFrame className="mt-2 w-full rounded-lg" busy={submitting}>
              <Button
                className="hover:bg-primary-hover h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold"
                disabled={submitting}
                onClick={() => void submit()}
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <BellOff className="size-4" aria-hidden />
                )}
                <Trans>Confirm opt-out</Trans>
              </Button>
            </BeamFrame>
          </div>
        )}
      </div>
    </main>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, state icon in a toned tile, display heading. Presentational
   only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};
const STATUS_CARD =
  "border-border bg-card w-full max-w-md rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8";
const STATUS_TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
const STATUS_BODY = "puls-reveal text-muted-foreground m-0 text-sm leading-relaxed";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;

type StatusTone = "primary" | "success" | "destructive";

const TILE_TONE: Record<StatusTone, string> = {
  primary: "border-primary/20 bg-primary-soft text-primary",
  success: "border-success-border bg-success-soft text-success",
  destructive: "border-destructive-border bg-destructive-soft text-destructive",
};

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function StatusTile({ icon: Icon, tone }: { icon: LucideIcon; tone: StatusTone }) {
  return (
    <span
      aria-hidden="true"
      className={`puls-reveal grid size-14 shrink-0 place-items-center rounded-2xl border ${TILE_TONE[tone]}`}
    >
      <Icon className="size-6" />
    </span>
  );
}
